/**
 * sparkline (#2388, "Image-#11") — pure geometry for the tile sparkline: a
 * muted full-history polyline with the RECENT segment split out so the tile can
 * tint it green/red versus its baseline. No SVG/React here — this returns path
 * strings + the trend classification; the ``Sparkline`` component renders them.
 *
 * Two entrypoints share the same geometry core:
 *   - {@link buildSparkline}       — bare ``number[]`` values laid out INDEX-spaced
 *                                    (retained for existing callers/tests).
 *   - {@link buildDatedSparkline}  — dated points laid out TIME-proportionally,
 *                                    windowed, and broken into PATH RUNS at gaps.
 *
 * ── HOF-93 traded the #2489 correspondence away, deliberately ────────────────
 *
 * #2489 made the tile mini *"a faithful small copy of the detail 'Alle zusammen
 * / 1J' curve"*: the same 365-day window, time-proportional x, and every gap
 * preserved as a straight interpolated segment. The second half of that is what
 * made the tiles unreadable. ``coreSparkline`` drew ONE continuous polyline
 * through every windowed point, so a months-long hole rendered as a long
 * straight diagonal — a price trend no observation supports, on the tile a
 * farmer prices against. All four Beerenobst tiles showed it; Heidelbeeren
 * (``Stand 25.07.25``) was the degenerate case, a mostly-empty window drawn as a
 * flat line with one spike.
 *
 * So the tile mini is **no longer a faithful miniature of the detail curve**,
 * and that is the point rather than an oversight:
 *
 *  - it uses {@link SPARKLINE_TILE_WINDOW_DAYS} (120 d), not the detail's 1J; and
 *  - it BREAKS at {@link SPARKLINE_GAP_DAYS} instead of bridging.
 *
 * The icon is a glanceable trend indicator, not a small copy of the chart; the
 * chart is one click away and has axes, a legend and a granularity switcher to
 * say what the icon cannot. What was NOT traded away is time-proportional x: a
 * run after a gap still starts where it actually falls. Compressing empty
 * periods out of the layout would make a spike two days ago render identically
 * to one two months ago — a worse lie than the one being fixed.
 *
 * **The detail chart is untouched, and it was never the interpolating one.**
 * ``aggregateChartModel.GAP_BREAK_DAYS`` has split the detail line at 42-day
 * gaps since #2543, with a carry-forward bridge since #2652. The tile was the
 * last surface still drawing through its holes; HOF-93 gives it its own,
 * tighter threshold rather than inventing the idea.
 */
import { toEpochDay } from './priceAt';

export type Trend = 'up' | 'down' | 'flat';

export interface SparklineModel {
  /** Polyline over the full history (drawn muted). Empty string when nothing is drawable. */
  historyPath: string;
  /** Polyline over the recent segment (drawn tinted). Shares its first vertex with history. */
  recentPath: string;
  trend: Trend;
  hasData: boolean;
  pointCount: number;
  /** Value at the start of the recent segment (the tint baseline), or null. */
  recentStartValue: number | null;
  /** Most recent value, or null. */
  lastValue: number | null;
}

export interface SparklineOptions {
  width?: number;
  height?: number;
  /** Vertical inset so the stroke never clips at the edges. */
  padding?: number;
  /** Fraction of the tail treated as "recent" (min 2 points). */
  recentFraction?: number;
  /** Relative tolerance under which a change reads as flat. */
  flatTolerance?: number;
}

/** A dated observation for the time-windowed sparkline (ISO ``YYYY-MM-DD`` + value). */
export interface DatedPoint {
  date: string;
  value: number;
}

export interface DatedSparklineOptions extends SparklineOptions {
  /** Lookback window in days from the latest point (default 365 = the detail "1J" period). */
  windowDays?: number;
  /**
   * Break the line when two consecutive points are MORE than this many days
   * apart (default {@link SPARKLINE_GAP_DAYS}). Strictly greater: a gap of
   * exactly ``gapDays`` still draws through.
   */
  gapDays?: number;
}

const DEFAULTS = { width: 120, height: 32, padding: 3, recentFraction: 0.3, flatTolerance: 0.001 };

/**
 * The general dated window — the detail chart's "1J" period (#2489).
 *
 * Unchanged at 365, and deliberately so: the tile no longer uses it (see
 * {@link SPARKLINE_TILE_WINDOW_DAYS}), and every other caller keeps exactly the
 * behaviour it had.
 */
export const SPARKLINE_WINDOW_DAYS = 365;

/**
 * The TILE mini's own window (HOF-93) — "a few months", per Antonio.
 *
 * 120 days rather than 90 because 90 clips the start of a German berry season
 * when the tile is read in late August, which is precisely when it is read. It
 * stays anchored on the ITEM's own latest observation rather than on today, so
 * a stale item (Heidelbeeren, ``Stand 25.07.25``) still shows the shape of what
 * it last did instead of blanking — the ``Stand`` line beside it already carries
 * the recency claim, so the curve does not have to make it twice.
 */
export const SPARKLINE_TILE_WINDOW_DAYS = 120;

/**
 * Two missed weeks (HOF-93). Invoice-derived prices for a selling berry farm
 * arrive at least weekly in season, so 14 days is unambiguously an ABSENCE
 * rather than a slow week.
 *
 * Fixed rather than adaptive (k × median spacing) on purpose: an adaptive
 * threshold would apply different rules to visually identical gaps on two tiles
 * sitting side by side in the same grid.
 */
export const SPARKLINE_GAP_DAYS = 14;

const EMPTY_MODEL: SparklineModel = {
  historyPath: '',
  recentPath: '',
  trend: 'flat',
  hasData: false,
  pointCount: 0,
  recentStartValue: null,
  lastValue: null,
};

interface CoreOptions {
  height: number;
  padding: number;
  recentFraction: number;
  flatTolerance: number;
  /**
   * Indices at which a new path run begins — the point after a gap. Index 0 is
   * implicit. Absent means ONE run over the whole series, which is the
   * pre-HOF-93 behaviour {@link buildSparkline} keeps.
   */
  runStarts?: ReadonlySet<number>;
  /**
   * Draw a run of a single point as a zero-length stroke (``M x,y L x,y``)
   * instead of omitting it (HOF-93 AC 15).
   *
   * Breaking the line at gaps ISOLATES points, and a bare ``M x,y`` renders
   * nothing at all in SVG — so without this the fix would silently delete every
   * lone observation from the tile, which is a worse regression than the
   * interpolation it removes. The stylesheet's ``stroke-linecap: round`` turns
   * the zero-length stroke into a dot.
   */
  markLoneRuns?: boolean;
}

/**
 * Shared geometry core: given values (oldest → newest) and their pre-computed
 * x-positions, build the two-layer path + trend classification. The entrypoints
 * differ in how they place x (index-spaced vs time-proportional) and in whether
 * they break the line into runs.
 */
function coreSparkline(values: number[], xs: number[], opts: CoreOptions): SparklineModel {
  const n = values.length;
  if (n === 0) return { ...EMPTY_MODEL };
  const { height, padding, recentFraction, flatTolerance, runStarts, markLoneRuns = false } = opts;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min;
  const usableH = height - padding * 2;

  // y inverted (high value → low y); flat series sit on the mid-line.
  const yAt = (v: number): number => (span === 0 ? height / 2 : padding + usableH * (1 - (v - min) / span));

  const vertex = (i: number): string => `${xs[i].toFixed(2)},${yAt(values[i]).toFixed(2)}`;

  /** One uninterrupted run, ``[from, to)``. */
  const runSegment = (from: number, to: number): string => {
    if (to - from === 1) {
      // A lone point: a visible zero-length mark, or nothing at all (legacy).
      return markLoneRuns ? `M${vertex(from)} L${vertex(from)}` : '';
    }
    const parts: string[] = [];
    for (let i = from; i < to; i += 1) parts.push(`${i === from ? 'M' : 'L'}${vertex(i)}`);
    return parts.join(' ');
  };

  /**
   * ``[from, to)`` as one or more runs, split wherever a gap was recorded.
   *
   * Applied to the recent slice as well as the whole history, which is what
   * keeps the tinted layer honest: it must never draw a connecting line the
   * muted layer refuses to draw (HOF-93 AC 16). Within a run the recent path
   * still opens on a vertex history already has, so the tint continues the
   * muted line seamlessly rather than starting beside it.
   */
  const toPath = (from: number, to: number): string => {
    const runs: string[] = [];
    let runFrom = from;
    for (let i = from + 1; i < to; i += 1) {
      if (runStarts?.has(i)) {
        runs.push(runSegment(runFrom, i));
        runFrom = i;
      }
    }
    runs.push(runSegment(runFrom, to));
    return runs.filter(Boolean).join(' ');
  };

  const recentCount = Math.min(n, Math.max(2, Math.ceil(n * recentFraction)));
  const recentStart = Math.max(0, n - recentCount);
  const recentStartValue = values[recentStart];
  const lastValue = values[n - 1];

  let trend: Trend = 'flat';
  if (recentStartValue !== 0) {
    const rel = (lastValue - recentStartValue) / Math.abs(recentStartValue);
    if (rel > flatTolerance) trend = 'up';
    else if (rel < -flatTolerance) trend = 'down';
  } else if (lastValue > 0) {
    trend = 'up';
  } else if (lastValue < 0) {
    trend = 'down';
  }

  return {
    historyPath: toPath(0, n),
    recentPath: toPath(recentStart, n),
    trend,
    hasData: true,
    pointCount: n,
    recentStartValue,
    lastValue,
  };
}

/**
 * Build the sparkline geometry for a series' bare values (oldest → newest),
 * INDEX-spaced across the width (a single point sits mid-width).
 *
 * Unchanged by HOF-93, which is deliberate: index-spaced values carry no dates,
 * so there is no gap to detect, and a lone value here still yields no drawable
 * path exactly as it always has.
 */
export function buildSparkline(values: number[], options: SparklineOptions = {}): SparklineModel {
  const { width, height, padding, recentFraction, flatTolerance } = { ...DEFAULTS, ...options };
  const n = values.length;
  if (n === 0) return { ...EMPTY_MODEL };
  const xs = values.map((_, i) => (n === 1 ? width / 2 : (i / (n - 1)) * width));
  return coreSparkline(values, xs, { height, padding, recentFraction, flatTolerance });
}

/**
 * Build the sparkline from DATED points: keep only points within
 * ``[latest − windowDays, latest]``, lay them out TIME-proportionally, and break
 * the line into separate path runs wherever consecutive points are more than
 * ``gapDays`` apart.
 *
 * **The break is the HOF-93 half.** Before it, this drew one polyline through
 * every windowed point, so a months-long hole became a single straight
 * interpolated segment — the artefact that made the Beerenobst tiles
 * unreadable. Each run is now its own ``M``, and a run of one point is drawn as
 * a zero-length stroke so an isolated observation stays on screen rather than
 * being deleted by the fix.
 *
 * **x remains time-proportional**, deliberately: the run after a gap starts at
 * its true position, so the emptiness is still legible as WIDTH even though no
 * line crosses it. See the module docstring for why compressing it out was
 * rejected, and for why the tile is no longer a small copy of the detail curve.
 */
export function buildDatedSparkline(points: DatedPoint[], options: DatedSparklineOptions = {}): SparklineModel {
  const { width, height, padding, recentFraction, flatTolerance, windowDays, gapDays } = {
    ...DEFAULTS,
    windowDays: SPARKLINE_WINDOW_DAYS,
    gapDays: SPARKLINE_GAP_DAYS,
    ...options,
  };
  if (points.length === 0) return { ...EMPTY_MODEL };

  const sorted = [...points].sort((a, b) => toEpochDay(a.date) - toEpochDay(b.date));
  const latestE = toEpochDay(sorted[sorted.length - 1].date);
  const startE = latestE - windowDays;
  const windowed = sorted.filter((p) => {
    const e = toEpochDay(p.date);
    return e >= startE && e <= latestE;
  });
  if (windowed.length === 0) return { ...EMPTY_MODEL };

  const values = windowed.map((p) => p.value);
  // x time-proportional across the window; a zero-width window sits mid-width.
  const xs = windowed.map((p) => (windowDays <= 0 ? width / 2 : ((toEpochDay(p.date) - startE) / windowDays) * width));

  // STRICTLY greater, matching `aggregateChartModel.splitAtGaps`: a gap of
  // exactly the threshold is a spacing the data supports, not an absence.
  const runStarts = new Set<number>();
  for (let i = 1; i < windowed.length; i += 1) {
    if (toEpochDay(windowed[i].date) - toEpochDay(windowed[i - 1].date) > gapDays) runStarts.add(i);
  }

  return coreSparkline(values, xs, {
    height,
    padding,
    recentFraction,
    flatTolerance,
    runStarts,
    markLoneRuns: true,
  });
}
